import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Loader2, Save, ImageIcon, Trash2, Upload, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';

export function LetterheadTab() {
  const [data, setData] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  const loadLogo = useCallback(async () => {
    try {
      const r = await api.get('/correspondence/letterhead/logo-file', { responseType: 'blob' });
      setLogoUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(r.data); });
    } catch { setLogoUrl(null); }
  }, []);

  useEffect(() => {
    api.get('/correspondence/letterhead').then((r) => {
      setData(r.data);
      if (r.data.has_logo) loadLogo();
    });
  }, [loadLogo]);

  useEffect(() => () => {
    if (logoUrl) URL.revokeObjectURL(logoUrl);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/correspondence/letterhead', data);
      toast.success('Briefpapier gespeichert – wird ab sofort in allen PDFs verwendet');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post(
        '/correspondence/letterhead/preview',
        {
          sender_line: data.sender_line,
          phone: data.phone,
          email: data.email,
          website: data.website,
          footer: data.footer,
        },
        { responseType: 'blob' },
      );
      if (r.data?.type && r.data.type.includes('json')) {
        const text = await r.data.text();
        let detail = 'Vorschau fehlgeschlagen';
        try { detail = JSON.parse(text)?.detail || detail; } catch { /* ignore */ }
        throw new Error(detail);
      }
      setPdfUrl((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      });
    } catch (e) {
      let msg = 'Vorschau fehlgeschlagen';
      const d = e.response?.data;
      if (d instanceof Blob) {
        try { msg = JSON.parse(await d.text())?.detail || msg; } catch { /* ignore */ }
      } else if (typeof d?.detail === 'string') {
        msg = d.detail;
      } else if (e.message && e.message !== 'Request failed with status code 500') {
        msg = e.message;
      }
      toast.error(msg);
    } finally { setPreviewing(false); }
  };

  const downloadPreview = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = `Briefpapier_Vorschau_${(data.tenant_name || 'Mandant').replace(/\s+/g, '_')}.pdf`;
    a.click();
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/correspondence/letterhead/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo hochgeladen');
      setData((d) => ({ ...d, has_logo: true }));
      loadLogo();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Logo-Upload fehlgeschlagen');
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm('Logo wirklich entfernen?')) return;
    await api.delete('/correspondence/letterhead/logo');
    setData((d) => ({ ...d, has_logo: false }));
    if (logoUrl) URL.revokeObjectURL(logoUrl);
    setLogoUrl(null);
    toast.success('Logo entfernt');
  };

  if (!data) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-slate-400" /></div>;

  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="letterhead-tab">
      <Card className="border-slate-200">
        <CardContent className="p-5 space-y-3">
          <div className="text-sm font-semibold text-slate-800">Briefkopf – {data.tenant_name}</div>
          <p className="text-xs text-slate-500">Diese Angaben erscheinen im Kopf aller PDF-Dokumente (Rechnungen, Beitragsaufstellungen).</p>
          <div>
            <label className="text-xs font-medium text-slate-500">Absenderzeile (Straße, PLZ Ort)</label>
            <Input value={data.sender_line} onChange={set('sender_line')} placeholder="z. B. Nordwalder Str. 9, 48268 Greven" className="mt-1" data-testid="letterhead-sender-line" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Telefon</label>
              <Input value={data.phone} onChange={set('phone')} className="mt-1" data-testid="letterhead-phone" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">E-Mail</label>
              <Input value={data.email} onChange={set('email')} className="mt-1" data-testid="letterhead-email" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Internet</label>
            <Input value={data.website} onChange={set('website')} placeholder="www.musikschule.de" className="mt-1" data-testid="letterhead-website" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500">Fußzeile (unten auf jeder PDF-Seite, mehrzeilig)</label>
            <Textarea value={data.footer} onChange={set('footer')} rows={3} placeholder={'z. B. Musikschule gGmbH · Geschäftsführung: Max Mustermann\nAmtsgericht Musterstadt HRB 12345'} className="mt-1" data-testid="letterhead-footer" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={preview} disabled={previewing} data-testid="letterhead-preview-btn">
              {previewing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />} Vorschau
            </Button>
            <Button onClick={save} disabled={saving} data-testid="letterhead-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-5 space-y-3">
          <div className="text-sm font-semibold text-slate-800">Logo</div>
          <p className="text-xs text-slate-500">Erscheint oben links im Briefkopf der PDFs. PNG, JPEG oder WebP, max. 3 MB.</p>
          <div className="border border-dashed border-slate-300 rounded-lg p-6 flex items-center justify-center min-h-[140px] bg-slate-50" data-testid="letterhead-logo-preview">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="max-h-28 max-w-full object-contain" />
            ) : (
              <div className="text-center text-slate-400 text-sm">
                <ImageIcon className="h-8 w-8 mx-auto mb-2" /> Kein Logo hinterlegt
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer transition-colors">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {data.has_logo ? 'Logo ersetzen' : 'Logo hochladen'}
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} data-testid="letterhead-logo-upload" />
            </label>
            {data.has_logo && (
              <Button variant="outline" onClick={removeLogo} data-testid="letterhead-logo-remove-btn">
                <Trash2 className="h-4 w-4 mr-1.5" /> Entfernen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!pdfUrl} onOpenChange={(o) => { if (!o && pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null); } }}>
        <DialogContent className="max-w-4xl h-[88vh] flex flex-col" data-testid="letterhead-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-brand-red" /> Briefpapier-Vorschau
            </DialogTitle>
            <DialogDescription>
              Musterbrief mit aktuellem Logo, Absender und Fußzeile (auch ungespeicherte Formularwerte).
            </DialogDescription>
          </DialogHeader>
          {pdfUrl && (
            <iframe src={pdfUrl} title="Briefpapier-Vorschau" className="flex-1 w-full rounded border border-slate-200" data-testid="letterhead-preview-iframe" />
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); setPdfUrl(null); }} data-testid="letterhead-preview-close-btn">
              Schließen
            </Button>
            <Button onClick={downloadPreview} data-testid="letterhead-preview-download-btn">
              <Download className="h-4 w-4 mr-1.5" /> PDF herunterladen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
